import { XMarkIcon } from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/core/components/ui/sheet";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { formatDateRange } from "@/core/utils/date";
import type { EnrichedIcmStatementDetail } from "@/icm/types";
import { useSubmitTechFeedback } from "@/tech-feedback/query";
import { useGetMyUser } from "@/user/query";
import {
  buildFeedbackInquiry,
  getFeedbackValidationSchema,
  getTechnicianDisplayName,
} from "./utils";

export const TechnicianFeedbackSheet = ({
  open,
  onOpenChange,
  statement,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statement: EnrichedIcmStatementDetail | null;
}) => {
  const { selectedBusiness } = useBusiness();
  const { data: techUser } = useGetMyUser();
  const { mutate: submitFeedback, isPending } = useSubmitTechFeedback();

  if (!techUser) return null;

  const displayName = getTechnicianDisplayName(techUser);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="overflow-hidden rounded-t-2xl p-0 sm:mx-auto sm:max-h-[90vh] sm:max-w-[500px] sm:rounded-2xl sm:border [&>button:first-child]:hidden"
      >
        <Stack fullWidth gap="none" className="flex h-full flex-col">
          <SheetTitle className="sr-only">Dispute This Statement</SheetTitle>
          <SheetDescription className="sr-only">
            Submit a dispute about this pay statement
          </SheetDescription>
          <Stack gap="md" className="bg-bg-secondary/50 p-6" fullWidth>
            <Stack direction="row" justify="between" align="start" fullWidth>
              <Typography variant="titleW3">Dispute This Statement</Typography>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Close dispute sheet"
                onClick={() => onOpenChange(false)}
                className="h-6 w-6 shrink-0 p-0"
              >
                <XMarkIcon className="h-6 w-6 shrink-0" />
              </Button>
            </Stack>
            <Typography variant="bodyW2" textColor="secondary">
              Think something looks off? Describe what you believe is wrong and
              we'll review it. Your statement details will be attached
              automatically.
            </Typography>
            <Stack gap="none" fullWidth>
              <Typography variant="subtitleW3" textColor="tertiary">
                Sending as
              </Typography>
              <Typography variant="bodyW2">{displayName}</Typography>
              <Typography variant="bodyW3" textColor="secondary">
                {techUser.email}
              </Typography>
              {statement && (
                <Typography variant="bodyW3" textColor="secondary">
                  {selectedBusiness?.name} ·{" "}
                  {formatDateRange(
                    statement.period_start,
                    statement.period_end,
                  )}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Separator />

          <div className="w-full flex-1 overflow-hidden bg-card">
            <ScrollArea className="h-full w-full">
              <Formik
                initialValues={{ inquiry: "" }}
                validationSchema={getFeedbackValidationSchema({
                  statement,
                  businessName: selectedBusiness?.name,
                })}
                validateOnBlur={false}
                onSubmit={(values, { resetForm }) => {
                  submitFeedback(
                    {
                      name: displayName,
                      email: techUser.email,
                      inquiry: buildFeedbackInquiry({
                        inquiry: values.inquiry,
                        statement,
                        businessName: selectedBusiness?.name,
                      }),
                    },
                    {
                      onSuccess: () => {
                        toast.success("Dispute submitted.", {
                          description:
                            "We'll review your statement and follow up.",
                        });
                        resetForm();
                        onOpenChange(false);
                      },
                      onError: (error) => {
                        toast.error("Failed to submit feedback", {
                          description:
                            error.errors?.join(", ") ||
                            "Please try again later.",
                        });
                      },
                    },
                  );
                }}
              >
                {({ getFieldProps, errors, touched }) => (
                  <Form className="w-full">
                    <Stack gap="lg" fullWidth className="px-6 pb-16 pt-5">
                      <Stack gap="xs" fullWidth>
                        <div className="h-52 w-full [&>div]:h-full">
                          <Textarea
                            label="What looks wrong?"
                            required
                            placeholder="Describe what you think is incorrect or missing."
                            {...getFieldProps("inquiry")}
                          />
                        </div>
                        {touched.inquiry && errors.inquiry && (
                          <Typography
                            variant="bodyW2"
                            className="text-destructive"
                          >
                            {errors.inquiry}
                          </Typography>
                        )}
                      </Stack>
                      <Button type="submit" loading={isPending} fullWidth>
                        Submit Dispute
                      </Button>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </ScrollArea>
          </div>
        </Stack>
      </SheetContent>
    </Sheet>
  );
};
